import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { trpc } from '#app/lib/trpc-tanstack.tsx'

export function useLogin(options?: {
	onSuccess?: (data: { userId: string }) => void
	onError?: (error: unknown) => void
}) {
	const queryClient = useQueryClient()

	return useMutation({
		...trpc.user.login.mutationOptions(),
		onSuccess: async (data) => {
			// Clear all cached data to prevent stale UI after logout
			queryClient.clear()
			options?.onSuccess?.(data)
		},
		onError: (error) => {
			console.error('Login error:', error)
			options?.onError?.(error)
		},
	})
}

export function useLogout(options?: { onSuccess?: () => void }) {
	const queryClient = useQueryClient()

	return useMutation({
		...trpc.user.logout.mutationOptions(),
		onSuccess: async () => {
			// Clear all cached data to prevent stale UI after logout
			queryClient.clear()
			options?.onSuccess?.()
		},
		onError: (error) => {
			console.error('Logout error:', error)
		},
	})
}

export function useSignup(options?: {
	onSuccess?: (data: { userId: string; workspaceId?: string }) => void
	onError?: (error: unknown) => void
}) {
	const queryClient = useQueryClient()

	return useMutation({
		...trpc.user.signup.mutationOptions(),
		onSuccess: async (data) => {
			// Clear all cached data to prevent stale UI after signup
			queryClient.clear()
			options?.onSuccess?.(data)
		},
		onError: (error) => {
			console.error('Signup error:', error)
			options?.onError?.(error)
		},
	})
}

export function useResetPassword(options?: {
	onSuccess?: () => void
	onError?: (error: unknown) => void
}) {
	return useMutation({
		...trpc.user.resetPassword.mutationOptions(),
		onSuccess: () => {
			options?.onSuccess?.()
		},
		onError: (error) => {
			console.error('Reset password error:', error)
			options?.onError?.(error)
		},
	})
}

export function useForgotPassword(options?: {
	onSuccess?: () => void
	onError?: (error: unknown) => void
}) {
	return useMutation({
		...trpc.user.forgotPassword.mutationOptions(),
		onSuccess: () => {
			options?.onSuccess?.()
		},
		onError: (error) => {
			console.error('Forgot password error:', error)
			options?.onError?.(error)
		},
	})
}

// Helper to get auth error message
export function getAuthErrorMessage(error: unknown): string {
	if (error instanceof Error) {
		// Map common Better Auth errors to user-friendly messages
		const msg = error.message.toLowerCase()
		if (
			msg.includes('invalid email or password') ||
			msg.includes('invalid credentials')
		) {
			return 'Invalid email or password'
		}
		if (msg.includes('user not found')) {
			return 'No account found with this email'
		}
		if (
			msg.includes('email already exists') ||
			msg.includes('user already exists')
		) {
			return 'An account with this email already exists'
		}
		return error.message
	}
	if (error instanceof TRPCClientError) {
		return error.message
	}
	return 'An unexpected error occurred'
}
